const { nothing, html, ref, createRef, CustomHTMLElement, utils, translation } = lithtml;
const tag     = 'wave-html-editor';
const version = '0.9.20';

/** @type {Promise}  */
let scriptPromise;

let language = translation.language;

// Froala has defined the norwegian language translation as 'nb' instead of 'no'
language === 'no' && (language = 'nb');

/**
 * WaveHtmlEditor ('wrapper' around the Froala editor))
 */
class WaveHtmlEditor extends CustomHTMLElement {
    static get tag()     { return tag     }
    static get version() { return version }

    /** Make sure changing the attributes and properties trigger a rerender */
    static get observedAttributes() { return ['isolated']; }
    static get observedProperties() { return []; }

    /**
     * Constructor
     */
    constructor() {
        super(WaveHtmlEditor, { useShadowRoot: false });
    }

    /**
     * Initalize variables
     */
    initalize() {
        const self = this;

        // We're calling initalize also from onConnected so we need to make sure we do not initalize
        // twice here (but if onDisconnected has been called, then we've cleared the previous initialization
        // and we can safely re-initalize)
        if (self._au) {
            return self;
        }

        self._av = null;
        self._aw     = null;
        self._au      = createRef();
        self._ax          = false;
        self.editorOptions       = utils.clone(defaultOptions);
        self.editorReady         = new Promise((resolve) => { self._av = resolve });
        self.editor              = null;

        // Support setting initial children in the component as the content of the editor
        self.editorReady.then(() => {
            const children = [...self.children].splice(self._aw ? 0 : 1);

            if (children.length) {
                self.editor.html.set(children.reduce((prev, curr) => prev + curr.outerHTML, ''));
            }
        });

        return self;
    }

    /**
     * Attributes of the component is not available before this callback, so we 'initalize' the code for both
     * isolated and not isolated editor here. The isolated editor lives fully in an iframe, and not isolated
     * is directly a part of the page where the component is used (both Froala style and scripts)
     */
    onFirstConnected() {
        this._ay();
    }

    /**
     * Called each time the component is attached to the DOM
     */
    onConnected() {
        const self = this;

        // If the component has been disconnected, then we need to set up the editor again.
        if (self._ax) {
            return;
        }

        self
            .initalize()
            .forceRender()   // Need to call forceRender here to make sure that _au has a reference to the editor parent element
            ._ay();
    }

    /**
     * Called each time the component is detached from the DOM, so we make sure
     * to clean up used resources here.
     */
    onDisconnected() {
        const self = this;

        self.editor && self.editor.destroy();

        if (self._aw) {
            self.shadowRoot.removeChild(self._aw);
        }

        self._av = null;
        self._aw     = null;
        self._au      = null;
        self._ax          = false;
        self.editorOptions       = null;
        self.editor              = null;
    }

    /**
     * Called when 'isolated' changes value
     * @param {boolean} oldValue the old value
     * @param {boolean} newValue the new value
     */
    onChanged_isolated(oldValue, newValue) {
        if (newValue) {
            this.options.useShadowRoot = true;
        }
    }

    /**
     * Render the template
     */
    render(reason) {
        let self = this;

        return self.$attribsLC.isolated
            ? null
            : html`<div style="display:flex;flex-direction:column;width:100%;height:100%;cursor:text" ${ref(self._au)}></div>`;
    }

    /**
     * Set up the editor
     */
    _ay() {
        const self    = this;
        const attribsLC = self.$attribsLC;
        const options = utils.clone(preDefOptions[attribsLC.config] || preDefOptions.default);
console.log(self.$attribsLC);
console.log(self.$attribs);
        // Read and use any options from the component's attributes
        updateToolbarButtons (options, 'moreText'     , attribsLC.toolbartext);
        updateToolbarButtons (options, 'moreParagraph', attribsLC.toolbarparagraph);
        updateToolbarButtons (options, 'moreMisc'     , attribsLC.toolbarmisc);
        updateToolbarButtons (options, 'moreRich'     , attribsLC.toolbarrich);

        if (attribsLC.toolbaroptions) {
            Object.assign(options, attribsLC.toolbaroptions);
        }

        self.editorOptions = options;

        options.key = attribsLC.serialkey || '';

        // If the editor is not isolated, add scripts and styles to the current page...
        if (!attribsLC.isolated) {
            self._az().then(() => {
                FroalaEditor.LANGUAGE.nb = languages.nb;
                self.editor = new FroalaEditor(self._au.value, options, () => {
                    self._av(self.editor);
                });
            });
        }
        // .. else make sure we instantiate it inside an iframe to isolate the editor completely from the rest of the page
        else {
            // Add styling to the component
            let style = document.createElement('style');

            style.textContent = ':host{width:100%;height:100%;overflow:hidden;display:block;}';
            self.shadowRoot.appendChild(style);

            // Give access to the component etc. from inside the iframe to be created
            const winComponentId = utils.newId('waveHtmlEditor_');
            window[winComponentId] = {
                options,
                component: self,
                resolve  : self._av,
                languages
            }

            // Iframe containing the editor
            const iframeEl      = self._aw = document.createElement('iframe');
            const editorBaseUrl = 'https://cdn.geminisuite.com/scripts/froala/4.3.1/';

            iframeEl.style  = 'width:100%;height:100%;border:0;margin:0;padding:0;overflow:hidden;';
            iframeEl.title  = 'html editor'
            iframeEl.srcdoc = `
<!DOCTYPE html>
<html lang="en">
<head>
    <link rel="stylesheet" href="https://cdn.geminisuite.com/wave/1.15.5/wave.min.css" />
    <link rel="stylesheet" href="${editorBaseUrl}css/froala_editor.pkgd.min.css" />
    <script src="${editorBaseUrl}js/froala_editor.pkgd.min.js" crossOrigin="anonymous" integrity="sha512-YzvgUBEAE1xnIRh7+RR3ouUzo3WVHGoJrTPTdittmlB/2h3uMS7zhGFbn+wj4utQltjkqNPN8Jz5UDJS9Uv70w=="></script>
    ${language !== 'en' && language !== 'nb' ? `<script src="${editorBaseUrl}js/languages/${language}.js" crossOrigin="anonymous"></script>` : ''}
    <style>
        html,body{margin:0;height:100%;width:100%;display:flex;overflow:hidden;}
        .fr-wrapper{flex-grow:1;overflow:auto;cursor:text;}
        .fr-dropdown-menu{bottom:initial!important;top: 40px;}
    </style>
</head>
<body>
    <div style="flex-grow:1;display:flex;flex-direction:column;"></div>
    <script>
        var x=parent['${winComponentId}'];
        FroalaEditor.LANGUAGE.nb=x.languages.nb;
        x.component.editor=new FroalaEditor('div', x.options, function () {x.resolve(x.component.editor)});
        delete parent['${winComponentId}'];
    </script>
</body>
</html>`.replace(/  |\n/g, '');
            self.shadowRoot.appendChild(iframeEl);
        }

        self._ax = true;
    }

    /**
     * Loads the requiered Froala sricpts, css, ...
     */
    _az() {
        if (scriptPromise) {
            return scriptPromise;
        }

        return scriptPromise = new Promise((resolve) => {
            // If someone else has added the FroalaEditor library, make sure we do not download it again
            if (window.FroalaEditor) {
                resolve();
                return;
            }

            let link = document.createElement('link')

            link.rel         = 'stylesheet';
            link.href        = 'https://cdn.geminisuite.com/scripts/froala/4.3.1/css/froala_editor.pkgd.min.css';
            link.id          = 'wave-html-editor-style';
            link.crossOrigin = 'anonymous';

            document.head.appendChild(link);

            let script = document.createElement('script');

            script.type        = 'text/javascript';
            script.src         = 'https://cdn.geminisuite.com/scripts/froala/4.3.1/js/froala_editor.pkgd.min.js';
            script.id          = 'wave-html-editor-script';
            script.crossOrigin = 'anonymous';
            script.async       = language === 'en' || language === 'nb';
            script.onload      = resolve;

            // INFO integrity: https://report-uri.com/home/sri_hash
            script.integrity = 'sha512-YzvgUBEAE1xnIRh7+RR3ouUzo3WVHGoJrTPTdittmlB/2h3uMS7zhGFbn+wj4utQltjkqNPN8Jz5UDJS9Uv70w==';

            document.head.appendChild(script);

            if (language !== 'en' && language != 'nb') {
                script = document.createElement('script');

                script.type        = 'text/javascript';
                script.src         = 'https://cdn.geminisuite.com/scripts/froala/4.3.1/js/languages/' + language + '.js';
                script.id          = 'wave-html-editor-lang-script';
                script.async       = false;
                script.crossOrigin = 'anonymous';

                document.head.appendChild(script);
            }
        });
    }
}

/* Define the new web component */
if (!window.customElements.get(tag)) {
    window.customElements.define(tag, WaveHtmlEditor);
}

/**
 * Update the Froala options object with new button arrrays
 * @param {Object} options the Froala options
 * @param {'moreText'|'moreParagraph'|'moreRich'|'moreMisc'} toolBarKey the
 * @param {string[]} buttons array of the buttons to show in the editor
 * @returns
 */
function updateToolbarButtons(options, toolBarKey, buttons) {
    if (!buttons) {
        return;
    }

    function updateButtonArray(toolbarButtons, buttons) {
        if (toolbarButtons) {
            toolbarButtons.length = 0;
            toolbarButtons.push(...buttons)
        }
    }

    updateButtonArray(options.toolbarButtons   && options.toolbarButtons  [toolBarKey].buttons, buttons);
    updateButtonArray(options.toolbarButtonsMD && options.toolbarButtonsMD[toolBarKey].buttons, buttons);
    updateButtonArray(options.toolbarButtonsSM && options.toolbarButtonsSM[toolBarKey].buttons, buttons);
    updateButtonArray(options.toolbarButtonsXS && options.toolbarButtonsXS[toolBarKey].buttons, buttons);
}

let defaultOptions = {
  //initOnClick             : true,       // Shows no toolbar before clicking on the editor
    theme                   : 'powel',
    heightMin               : '100%',
    language                : language,
    attribution             : false,
    useClasses              : true,       /* This must be true to limit amount of generated HTML - We always have css classes available when using this so no need to import styles inline. */
    htmlUntouched           : true,
    imageMaxSize            : 100 * 1024, // 100kB (has been 5MB - reset to this when handling large size better in data flow)
    charCounterCount        : false,
    fontSizeUnit            : 'px',
    fontSize                : ['8', '10', '12', '14', '16', '18', '20', '22', '24', '26', '28', '30', '32', '36', '48', '72'],
    fontSizeDefaultSelection: '14',                                                                            // 15px = 11pt
    fontSizeSelection       : true,
    fontFamily: {
        'Arial,Helvetica,sans-serif'   : 'Arial',
        'Georgia,serif'                : 'Georgia',
        'Impact,Charcoal,sans-serif'   : 'Impact',
        'Tahoma,Geneva,sans-serif'     : 'Tahoma',
        "'Times New Roman',Times,serif": 'Times New Roman',
        'Verdana,Geneva,sans-serif'    : 'Verdana',
        'Calibri,Candara,sans-serif'   : 'Calibri'
    },
    linkText       : false,
    linkAlwaysBlank: true,
    linkAutoPrefix : 'https://',
    linkEditButtons: ['linkOpen', 'linkStyle'/*, 'linkEdit', 'linkRemove'*/],
    linkStyles: {
        // Froala default:
        // 'fr-green' : 'Green',
        // 'fr-strong': 'Thick',

        // Powel-UI:
        blue100  : 'Blue',
        linkClean: 'No underline',
    },
    linkMultipleStyles: true,
    tableCellStyles: {
        'fr-highlighted': 'Highlighted',
        'fr-thick'      : 'Thick',
        'bdrAs'         : 'Bordered'
    },
    tableStyles: {
        'fr-dashed-borders': 'Dashed Borders',
        'fr-solid-borders' : 'Solid Borders',
        'fr-row-borders'   : 'Row Borders',
        'fr-alternate-rows': 'Alternate Rows'
    },
    toolbarButtons: {
        moreText: {
            buttons: ['insertMergeField',
                'bold',
                'italic',
                'underline',
                'fontFamily',
                'fontSize',
                'textColor',
                'backgroundColor' /*, 'inlineClass', 'inlineStyle'*/,
                'strikeThrough',
                'subscript',
                'superscript',
                'clearFormatting'
            ],
            buttonsVisible: 7
        },
        moreParagraph: {
            buttons: [
                'align',
                'formatOL',
                'formatUL',
                'paragraphFormat',
                'paragraphStyle',
                'lineHeight',
                'outdent',
                'indent',
                'quote'
            ],
            buttonsVisible: 5
        },
        moreRich: {
            buttons       : [ 'insertHR', 'insertImage', 'insertTable', 'specialCharacters' /*, 'insertLink' */ ],
            buttonsVisible: 4
        },
        moreMisc: {
            buttons       : [ 'undo', 'redo', 'pdfPreview', 'fullscreen', 'selectAll', 'html' ],
            align         : 'right',
            buttonsVisible: 3
        }
    },
    quickInsertButtons: ['image', 'table', 'ul', 'ol', 'hr', 'insertSignature' ],
    linkInsertButtons:  ['linkBack'],
    pluginsEnabled:     ['codeMirror', 'align', 'charCounter','codeBeautifier', 'codeView', 'colors', 'draggable', 'emoticons', 'fontAwesome', 'fontFamily', 'fontSize',
                         'image','lineBreaker', 'lineHeight', 'link', 'lists', 'paragraphFormat', 'paragraphStyle', 'quickInsert', 'quote', 'table', 'url', 'wordPaste', 'fullscreen'
                      /*,'video', 'save', 'entities', 'file', 'embedly', 'imageTUI', 'imageManager', 'inlineStyle', 'inlineClass'*/ ],
    events: {
        //'codeView.update': function () {
        //    console.log(this);
        //  },
        'image.beforeUpload': function(files) {
            var editor = this;
            if (files.length) {
                // Create a File Reader.
                var reader = new FileReader();
                // Set the reader to insert images when they are loaded.
                reader.onload = function(e) {
                    var result = e.target.result;
                    console.log('imageMaxSize', editor.opts.imageMaxSize, 'actual size:', e.total);
                    if (editor.opts.imageMaxSize && editor.opts.imageMaxSize < e.total) {
                        editor.modals.get('imgTooLargeModal') ||
                        editor.modals.create(
                            'imgTooLargeModal',
                            '<h1>Bilde for stort</h1>',
                            '<p style="padding-top: 10px;">Bildet er for stort for å kunne settes inn (' +
                                formatBytes(e.total) +
                                ').</p>' +
                                '<p>Bilder som legges til må ikke være større enn ' +
                                formatBytes(editor.opts.imageMaxSize) +
                                '.</p>'
                        );
                        editor.modals.show('imgTooLargeModal');

                        editor.image.remove();

                        // Make sure it is not possible to undo removal of image
                        var undoStateAfterImageRemoved = editor.undo_stack.pop(); // Get the state without image
                        editor.undo_stack.pop(); // Remove the state with image
                        editor.undo_stack.push(undoStateAfterImageRemoved); // Add the state without image

                    } else {
                        editor.image.insert(result, null, null, editor.image.get());
                    }
                };
                // Read image as base64.
                reader.readAsDataURL(files[0]);
            }
            editor.popups.hideAll();
            // Stop default upload chain.
            return false;
        }
    }
};

// Make sure the toolbar on small screen also works ok
let tbXS = defaultOptions.toolbarButtonsXS = utils.clone(defaultOptions.toolbarButtons);
tbXS.moreText.buttonsVisible      = 2;
tbXS.moreParagraph.buttonsVisible = 2;
tbXS.moreRich.buttonsVisible      = 2;
tbXS.moreMisc.buttonsVisible      = 2;

let plainOptions = utils.clone(defaultOptions);

plainOptions.iframe     = false;
plainOptions.imagePaste = false;

let tb = plainOptions.toolbarButtons;
tbXS   = plainOptions.toolbarButtonsXS;

tb.moreMisc.buttons             = tbXS.moreMisc.buttons      = [ 'undo','redo' ];
tb.moreRich.buttons             = tbXS.moreRich.buttons      = [ 'insertHR','insertTable','specialCharacters' ];
tb.moreText.buttons             = tbXS.moreText.buttons      = [ 'bold','italic','underline','fontFamily','fontSize','textColor','backgroundColor','strikeThrough','clearFormatting' ];
tb.moreParagraph.buttons        = tbXS.moreParagraph.buttons = [ 'align','formatOL','formatUL','paragraphFormat','outdent','indent'],
tbXS.moreMisc.buttons           = [];
tb.moreText.buttonsVisible      = 7;
tb.moreParagraph.buttonsVisible = 4;
plainOptions.quickInsertButtons = [ 'table', 'ul', 'ol', 'hr' ];

/**
 * Easy access to predefined options
 */
let preDefOptions = {
    default: defaultOptions,
    plain  : plainOptions
}

function formatBytes(bytes) {
    let unit  = 'B';
    let units = [ 'TB', 'GB', 'MB', 'kB' ];
    let convertedBytes = Math.round(bytes);

    while (convertedBytes > 1024.0 && units.length > 0) {
        convertedBytes /= 1024.0;
        convertedBytes = Math.round(convertedBytes * 10.0) / 10.0;
        unit = units.pop();
    }

    return convertedBytes + ' ' + unit;
}

const languages = {
    nb: {
        translation: {
            // Place holder
            'Type something': 'Skriv noe',
            // Basic formatting
            'Bold'         : 'Fet',
            'Italic'       : 'Kursiv',
            'Underline'    : 'Understreket',
            'Strikethrough': 'Gjennomstreket',
            // Main buttons
            'Insert': 'Sett',
            'Delete': 'Slett',
            'Cancel': 'Avbryt',
            'OK'    : 'OK',
            'Back'  : 'Tilbake',
            'Remove': 'Fjern',
            'More'  : 'Mer',
            'Update': 'Oppdatering',
            'Style' : 'Stil',
            // Font
            'Font Family': 'Skriftsnitt',
            'Font Size'  : "St\xF8rrelse",
            // Colors
            'Colors'    : 'Farger',
            'Background': 'Bakgrunn',
            'Text'      : 'Tekst',
            'HEX Color' : 'Heksefarge',
            // Paragraphs
            'Paragraph Format': 'Stiler',
            'Normal'          : 'Normal',
            'Code'            : 'Kode',
            'Heading 1'       : 'Overskrift 1',
            'Heading 2'       : 'Overskrift 2',
            'Heading 3'       : 'Overskrift 3',
            'Heading 4'       : 'Overskrift 4',
            // Style
            'Paragraph Style': 'Avsnittsstil',
            'Inline Style'   : "P\xE5 linje stil",
            // Alignment
            'Align'        : 'Justering',
            'Align Left'   : 'Venstrejustert',
            'Align Center' : 'Midtstilt',
            'Align Right'  : "H\xF8yrejustert",
            'Align Justify': 'Juster alle linjer',
            'None'         : 'None',
            // Lists
            'Ordered List'  : 'Ordnet liste',
            'Unordered List': 'Uordnet liste',
            // Indent
            'Decrease Indent': 'Reduser innrykk',
            'Increase Indent': "\xD8k innrykk",
            // Links
            'Insert Link'    : 'Sett inn lenke',
            'Open in new tab': "\xC5pne i ny fane",
            'Open Link'      : "\xC5pne lenke",
            'Edit Link'      : 'Rediger lenke',
            'Unlink'         : 'Fjern lenke',
            'Choose Link'    : 'Velge lenke',
            // Images
            'Insert Image'                           : 'Sett inn bilde',
            'Upload Image'                           : 'Last opp bilde',
            'By URL'                                 : 'Ved URL',
            'Browse'                                 : 'Bla',
            'Drop image'                             : 'Slippe bilde',
            'or click'                               : 'eller klikk',
            'Manage Images'                          : 'Bildebehandling',
            'Loading'                                : 'Lasting',
            'Deleting'                               : 'Slette',
            'Tags'                                   : 'Tags',
            'Are you sure? Image will be deleted.'   : 'Er du sikker? Bildet vil bli slettet.',
            'Replace'                                : 'Erstatte',
            'Uploading'                              : 'Opplasting',
            'Loading image'                          : 'Lasting bilde',
            'Display'                                : 'Utstilling',
            'Inline'                                 : "P\xE5 linje",
            'Break Text'                             : 'Brudd tekst',
            'Alternative Text'                       : 'Alternativ tekst',
            'Change Size'                            : "Endre st\xF8rrelse",
            'Width'                                  : 'Bredde',
            'Height'                                 : "H\xF8yde",
            'Something went wrong. Please try again.': "Noe gikk galt. V\xE6r s\xE5 snill, pr\xF8v p\xE5 nytt.",
            'Image Caption'                          : 'Bilde bildetekst',
            'Advanced Edit'                          : 'Avansert redigering',
            // Video
            'Insert Video'                              : 'Sett inn video',
            'Embedded Code'                             : 'Embedded kode',
            'Paste in a video URL'                      : 'Lim inn i en video-url',
            'Drop video'                                : 'Slipp video',
            'Your browser does not support HTML5 video.': 'Nettleseren din støtter ikke html5 video.',
            'Upload Video'                              : 'Last opp video',
            // Tables
            'Insert Table'        : 'Sett inn tabell',
            'Table Header'        : 'Tabell header',
            'Remove Table'        : 'Fjern tabell',
            'Table Style'         : 'Tabell stil',
            'Horizontal Align'    : 'Horisontal justering',
            'Row'                 : 'Rad',
            'Insert row above'    : "Sett inn rad f\xF8r",
            'Insert row below'    : 'Sett in rad etter',
            'Delete row'          : 'Slett rad',
            'Column'              : 'Kolonne',
            'Insert column before': "Sett inn kolonne f\xF8r",
            'Insert column after' : 'Sett inn kolonne etter',
            'Delete column'       : 'Slett kolonne',
            'Cell'                : 'Celle',
            'Merge cells'         : "Sl\xE5 sammen celler",
            'Horizontal split'    : 'Horisontalt delt',
            'Vertical split'      : 'Vertikal split',
            'Cell Background'     : 'Celle bakgrunn',
            'Vertical Align'      : 'Vertikal justering',
            'Top'                 : 'Topp',
            'Middle'              : 'Midten',
            'Bottom'              : 'Bunn',
            'Align Top'           : 'Justere toppen',
            'Align Middle'        : 'Justere midten',
            'Align Bottom'        : 'Justere bunnen',
            'Cell Style'          : 'Celle stil',
            // Files
            'Upload File': 'Opplastingsfil',
            'Drop file'  : 'Slippe fil',
  // Emoticons
  //            'Emoticons': 'Emoticons',
  //            'Grinning face': 'Flirer ansikt',
  //            'Grinning face with smiling eyes': "Flirer ansikt med smilende \xF8yne",
  //            'Face with tears of joy': "Ansikt med t\xE5rer av glede",
  //            'Smiling face with open mouth': "Smilende ansikt med \xE5pen munn",
  //            'Smiling face with open mouth and smiling eyes': "Smilende ansikt med \xE5pen munn og smilende \xF8yne",
  //            'Smiling face with open mouth and cold sweat': "Smilende ansikt med \xE5pen munn og kald svette",
  //            'Smiling face with open mouth and tightly-closed eyes': "Smilende ansikt med \xE5pen munn og tett lukkede \xF8yne",
  //            'Smiling face with halo': 'Smilende ansikt med glorie',
  //            'Smiling face with horns': 'Smilende ansikt med horn',
  //            'Winking face': 'Blunk ansikt',
  //            'Smiling face with smiling eyes': "Smilende ansikt med smilende \xF8yne",
  //            'Face savoring delicious food': "M\xF8te nyter deilig mat",
  //            'Relieved face': 'Lettet ansikt',
  //            'Smiling face with heart-shaped eyes': "Smilende ansikt med hjerteformede \xF8yne",
  //            'Smiling face with sunglasses': 'Smilende ansikt med solbriller',
  //            'Smirking face': 'Tilfreds ansikt',
  //            'Neutral face': "N\xF8ytral ansikt",
  //            'Expressionless face': "Uttrykksl\xF8st ansikt",
  //            'Unamused face': 'Ikke moret ansikt',
  //            'Face with cold sweat': 'Ansikt med kald svette',
  //            'Pensive face': 'Tankefull ansikt',
  //            'Confused face': 'Forvirret ansikt',
  //            'Confounded face': 'Skamme ansikt',
  //            'Kissing face': 'Kyssing ansikt',
  //            'Face throwing a kiss': 'Ansikt kaste et kyss',
  //            'Kissing face with smiling eyes': "Kyssing ansikt med smilende \xF8yne",
  //            'Kissing face with closed eyes': "Kyssing ansiktet med lukkede \xF8yne",
  //            'Face with stuck out tongue': 'Ansikt med stakk ut tungen',
  //            'Face with stuck out tongue and winking eye': "Ansikt med stakk ut tungen og blunke \xF8ye",
  //            'Face with stuck out tongue and tightly-closed eyes': "Ansikt med fast ut tungen og tett lukket \xF8yne",
  //            'Disappointed face': 'Skuffet ansikt',
  //            'Worried face': 'Bekymret ansikt',
  //            'Angry face': 'Sint ansikt',
  //            'Pouting face': 'Trutmunn ansikt',
  //            'Crying face': "Gr\xE5ter ansikt",
  //            'Persevering face': 'Utholdende ansikt',
  //            'Face with look of triumph': 'Ansikt med utseendet til triumf',
  //            'Disappointed but relieved face': 'Skuffet men lettet ansikt',
  //            'Frowning face with open mouth': "Rynke ansikt med \xE5pen munn",
  //            'Anguished face': 'Forpint ansikt',
  //            'Fearful face': 'Engstelig ansikt',
  //            'Weary face': 'Slitne ansiktet',
  //            'Sleepy face': "S\xF8vnig ansikt",
  //            'Tired face': "Tr\xF8tt ansikt",
  //            'Grimacing face': 'Griner ansikt',
  //            'Loudly crying face': "H\xF8ylytt gr\xE5tende ansikt",
  //            'Face with open mouth': "Ansikt med \xE5pen munn",
  //            'Hushed face': "Lavm\xE6lt ansikt",
  //            'Face with open mouth and cold sweat': "Ansikt med \xE5pen munn og kald svette",
  //            'Face screaming in fear': 'Ansikt skriker i frykt',
  //            'Astonished face': 'Forbauset ansikt',
  //            'Flushed face': 'Flushed ansikt',
  //            'Sleeping face': 'Sovende ansikt',
  //            'Dizzy face': 'Svimmel ansikt',
  //            'Face without mouth': 'Ansikt uten munn',
  //            'Face with medical mask': 'Ansikt med medisinsk maske',
  // Line breaker
            'Break': 'Brudd',
            // Math
            'Subscript'  : 'Senket skrift',
            'Superscript': 'Hevet skrift',
            // Full screen
            'Fullscreen': 'Full skjerm',
            // Horizontal line
            'Insert Horizontal Line': 'Sett inn horisontal linje',
            // Clear formatting
            'Clear Formatting': 'Fjerne formatering',
            // Save
            'Save': 'Lagre',
            // Undo, redo
            'Undo': 'Angre',
            'Redo': "Utf\xF8r likevel",
            // Select all
            'Select All': 'Marker alt',
            // Code view
            'Code View': 'Kodevisning',
            // Quote
            'Quote'   : 'Sitat',
            'Increase': "\xD8ke",
            'Decrease': 'Nedgang',
            // Quick Insert
            'Quick Insert': 'Hurtiginnsats',
            // Spcial Characters
            'Special Characters': 'Spesielle karakterer',
            'Latin'             : 'Latin',
            'Greek'             : 'Gresk',
            'Cyrillic'          : 'Kyrilliske',
            'Punctuation'       : 'Tegnsetting',
            'Currency'          : 'Valuta',
            'Arrows'            : 'Piler',
            'Math'              : 'Matte',
            'Misc'              : 'Misc',
            // Print.
            'Print': 'Skrive ut',
            // Spell Checker.
            'Spell Checker': 'Stavekontroll',
            // Help
            'Help'                             : 'Hjelp',
            'Shortcuts'                        : 'Snarveier',
            'Inline Editor'                    : 'Inline editor',
            'Show the editor'                  : 'Vis redaktøren',
            'Common actions'                   : 'Felles handlinger',
            'Copy'                             : 'Kopiere',
            'Cut'                              : 'Kutte opp',
            'Paste'                            : 'Lim inn',
            'Basic Formatting'                 : 'Grunnleggende formatering',
            'Increase quote level'             : 'Øke tilbudsnivået',
            'Decrease quote level'             : 'Redusere tilbudsnivå',
            'Image / Video'                    : 'Bilde / video',
            'Resize larger'                    : 'Endre størrelsen større',
            'Resize smaller'                   : 'Endre størrelsen mindre',
            'Table'                            : 'Bord',
            'Select table cell'                : 'Velg tabellcelle',
            'Extend selection one cell'        : 'Utvide valg en celle',
            'Extend selection one row'         : 'Utvide valg en rad',
            'Navigation'                       : 'Navigasjon',
            'Focus popup / toolbar'            : 'Fokus popup / verktøylinje',
            'Return focus to previous position': 'Returnere fokus til tidligere posisjon',
            // Embed.ly
            'Embed URL'              : 'Legge inn nettadressen',
            'Paste in a URL to embed': 'Lim inn i en URL for å legge inn',
            // Word Paste.
            'The pasted content is coming from a Microsoft Word document. Do you want to keep the format or clean it up?': 'Det limte innholdet kommer fra et Microsoft Word-dokument. Vil du beholde formatet eller rydde det opp?',
            'Keep'                                                                                                       : 'Beholde',
            'Clean'                                                                                                      : 'Ren',
            'Word Paste Detected'                                                                                        : 'Ordpasta oppdages',
            // Character Counter
            'Characters': 'Tegn',
            // More Buttons
            'More Text'     : 'Mer Tekst',
            'More Paragraph': 'Mer Avsnitt',
            'More Rich'     : 'Mer Rik',
            'More Misc'     : 'Mer Diverse'
        },
        direction: 'ltr'
    }
}
